import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, UserPlus, ShieldCheck, KeyRound, Infinity as InfinityIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format, addMonths } from "date-fns";

type Duration = "1" | "3" | "6" | "12" | "unlimited";

const DURATION_LABEL: Record<Duration, string> = {
  "1": "1 month",
  "3": "3 months",
  "6": "6 months",
  "12": "12 months",
  unlimited: "Unlimited",
};

const SUB_TYPE: Record<Duration, string> = {
  "1": "monthly",
  "3": "quarterly",
  "6": "biannual",
  "12": "annual",
  unlimited: "annual",
};

export default function UserManager() {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  // Grant Access dialog state
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantUser, setGrantUser] = useState<{ id: string; name: string } | null>(null);
  const [grantExam, setGrantExam] = useState<string>("");
  const [grantDuration, setGrantDuration] = useState<Duration>("1");

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: userRoles } = useQuery({
    queryKey: ["admin-user-roles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("user_roles").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: exams } = useQuery({
    queryKey: ["admin-active-exams"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_labels")
        .select("slug, name")
        .eq("is_active", true)
        .order("display_order");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: allAccess } = useQuery({
    queryKey: ["admin-all-user-access"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_exam_access")
        .select("user_id, exam_slug, expires_at, subscription_type");
      if (error) throw error;
      return data || [];
    },
  });

  const accessByUser = (userId: string) =>
    (allAccess || []).filter((a) => a.user_id === userId);

  const assignAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: "admin" });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Admin role assigned successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-user-roles"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to assign admin role: ${error.message}`);
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", "admin");
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Admin role removed successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-user-roles"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to remove admin role: ${error.message}`);
    },
  });

  const grantAccessMutation = useMutation({
    mutationFn: async (params: {
      userId: string;
      examSlug: string;
      duration: Duration;
    }) => {
      const now = new Date();
      const expiresAt =
        params.duration === "unlimited"
          ? new Date("2099-12-31T23:59:59Z")
          : addMonths(now, parseInt(params.duration, 10));

      const { error } = await supabase
        .from("user_exam_access")
        .upsert(
          {
            user_id: params.userId,
            exam_slug: params.examSlug,
            activated_at: now.toISOString(),
            expires_at: expiresAt.toISOString(),
            subscription_type: SUB_TYPE[params.duration],
            auto_renew: false,
            trial_started_at: null,
            trial_expires_at: null,
          },
          { onConflict: "user_id,exam_slug" }
        );
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      toast.success(
        `Granted ${DURATION_LABEL[vars.duration]} of ${vars.examSlug} access`
      );
      queryClient.invalidateQueries({ queryKey: ["admin-all-user-access"] });
      queryClient.invalidateQueries({ queryKey: ["admin-billing-subs"] });
      queryClient.invalidateQueries({ queryKey: ["admin-exam-subscriptions"] });
      setGrantOpen(false);
      setGrantUser(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to grant access: ${error.message}`);
    },
  });

  const isAdmin = (userId: string) =>
    userRoles?.some((r) => r.user_id === userId && r.role === "admin");

  const filteredProfiles = profiles?.filter(
    (p) =>
      p.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openGrant = (userId: string, name: string) => {
    setGrantUser({ id: userId, name });
    setGrantExam(exams?.[0]?.slug || "");
    setGrantDuration("1");
    setGrantOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            <CardTitle>User Management</CardTitle>
          </div>
        </div>
        <CardDescription>Manage user accounts, roles, and exam access</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Access</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : filteredProfiles?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredProfiles?.map((profile) => {
                const access = accessByUser(profile.id);
                return (
                  <TableRow key={profile.id}>
                    <TableCell>
                      <div className="font-medium">{profile.full_name || "No name"}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {profile.id.slice(0, 8)}...
                      </div>
                    </TableCell>
                    <TableCell>
                      {access.length === 0 ? (
                        <span className="text-xs text-muted-foreground">None</span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {access.map((a) => {
                            const exp = a.expires_at ? new Date(a.expires_at) : null;
                            const unlimited = exp && exp.getFullYear() >= 2099;
                            const expired = exp && exp < new Date();
                            return (
                              <Badge
                                key={a.exam_slug}
                                variant={expired ? "destructive" : "secondary"}
                                className="w-fit text-xs font-normal"
                              >
                                {a.exam_slug}:{" "}
                                {unlimited
                                  ? "Unlimited"
                                  : exp
                                  ? format(exp, "MMM d, yyyy")
                                  : "—"}
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {format(new Date(profile.created_at), "MMM dd, yyyy")}
                    </TableCell>
                    <TableCell>
                      {isAdmin(profile.id) ? (
                        <Badge variant="default">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline">User</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openGrant(profile.id, profile.full_name || "this user")}
                        >
                          <KeyRound className="h-4 w-4 mr-1" />
                          Grant Access
                        </Button>
                        {isAdmin(profile.id) ? (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm">Remove Admin</Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove Admin Role</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove admin privileges from{" "}
                                  {profile.full_name || "this user"}?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => removeAdminMutation.mutate(profile.id)}
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="default" size="sm">
                                <UserPlus className="h-4 w-4 mr-1" />
                                Make Admin
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Assign Admin Role</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to grant admin privileges to{" "}
                                  {profile.full_name || "this user"}? They will have full access
                                  to all admin features.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => assignAdminMutation.mutate(profile.id)}
                                >
                                  Assign
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Grant Access Dialog */}
        <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Grant Exam Access</DialogTitle>
              <DialogDescription>
                Give <strong>{grantUser?.name}</strong> paid access to an exam. This
                overwrites any existing access for the selected exam.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Exam</Label>
                <Select value={grantExam} onValueChange={setGrantExam}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select exam" />
                  </SelectTrigger>
                  <SelectContent>
                    {exams?.map((e) => (
                      <SelectItem key={e.slug} value={e.slug}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration</Label>
                <Select
                  value={grantDuration}
                  onValueChange={(v) => setGrantDuration(v as Duration)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="unlimited">
                      <span className="flex items-center gap-1">
                        <InfinityIcon className="h-3 w-3" />
                        Unlimited
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setGrantOpen(false)}>
                Cancel
              </Button>
              <Button
                disabled={!grantExam || grantAccessMutation.isPending}
                onClick={() =>
                  grantUser &&
                  grantAccessMutation.mutate({
                    userId: grantUser.id,
                    examSlug: grantExam,
                    duration: grantDuration,
                  })
                }
              >
                {grantAccessMutation.isPending ? "Granting..." : "Grant Access"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
